import axios from 'axios';
import { uploadMediaFile as uploadLargeMediaFile } from './mediaUploadService.js';
import { withCache, apiCache } from '../utils/apiCache';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const CONTENT_API_URL = process.env.REACT_APP_API_CONTENT_URL;

// Internal functions without caching
const _getBioVideo = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/videoList?videoType=bioVideo`, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    // Check if 'result' is present in the response and is an array
    if (response.data.result && Array.isArray(response.data.result) && response.data.result.length > 0) {
      return response.data.result[0];
    } else {
      console.error("No bio video found.");
      return null;
    }
  } catch (error) {
    console.error("Error fetching bio video:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error fetching bio video");
  }
};

const _getTopLevelVideos = async () => {
  const token = localStorage.getItem('token');

  try {
    const response = await axios.get(`${API_BASE_URL}/videoList?videoType=topLevelVideos`, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    // Check if 'result' is present in the response and is an array
    if (response.data.result && Array.isArray(response.data.result)) {
      return response.data.result;
    } else {
      console.error("No top level videos found.");
      return [];
    }
  } catch (error) {
    console.error("Error fetching top level videos:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Error fetching top level videos");
  }
};

// Cached versions with 3-minute TTL (media changes less frequently than user data)
export const getBioVideo = withCache(_getBioVideo, {
  ttl: 3 * 60 * 1000,
  key: 'bio-video'
});

export const getTopLevelVideos = withCache(_getTopLevelVideos, {
  ttl: 3 * 60 * 1000,
  key: 'top-level-videos'
});

// Function to upload a file (video, audio, image, etc.) - Enhanced for large files
export const uploadMediaFile = async (file, onProgress = null) => {
  try {
    
    // Use the enhanced upload service that handles large files automatically
    const result = await uploadLargeMediaFile(file, onProgress);
    
    // Return just the URL for backward compatibility with existing code
    const fileUrl = result.url || result;
    
    
    return fileUrl;
  } catch (error) {
    console.error("Error uploading media:", error);
    throw new Error(error.message || "Error uploading media");
  }
};

export const addBioVideo = async (file, thumbnailFile = null) => {
  const token = localStorage.getItem('token');

  try {

    // Step 1: Upload the file to get the file URL
    const uploadResult = await uploadMediaFile(file);

    // Extract the URL from the upload result
    const fileUrl = uploadResult.url || uploadResult;

    // Check if we have a valid URL
    if (!fileUrl) {
      throw new Error('Failed to get a valid URL from file upload');
    }

    // Step 1b (optional): upload a poster thumbnail so the player shows a frame
    // instantly. Best-effort — if it fails, we still save the video without it.
    let thumbnailUrl = null;
    if (thumbnailFile) {
      try {
        const thumbResult = await uploadMediaFile(thumbnailFile);
        thumbnailUrl = thumbResult.url || thumbResult || null;
      } catch (thumbErr) {
        console.warn('Bio video poster upload failed (saving video without poster):', thumbErr.message);
      }
    }

    // Step 2: Use the new addBioVideo endpoint
    const addBioVideoResponse = await axios.post(
      `${API_BASE_URL}/bioVideo`,
      {
        url: fileUrl,
        type: "video",
        thumbnail: thumbnailUrl
      },
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }
    );

    // Invalidate bio video cache after successful add
    apiCache.invalidate('bio-video');

    return addBioVideoResponse.data;
  } catch (error) {
    console.error("Error uploading bio video:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Upload failed");
  }
};

// Shared payload for qualification content updates (mirrors add — always sends comments string)
const buildQualificationUpdatePayload = (attachment, overrides = {}) => ({
  attachmentId: attachment.id,
  title: overrides.title !== undefined ? overrides.title : (attachment.title || ''),
  description: overrides.description !== undefined ? overrides.description : (attachment.description || ''),
  type: overrides.type !== undefined ? overrides.type : (attachment.type || ''),
  media_path: overrides.media_path !== undefined ? overrides.media_path : (attachment.media_path || ''),
  comments: overrides.comments !== undefined ? overrides.comments : (attachment.comments || ''),
  post_to: attachment.post_to || attachment.qualification_id || attachment.qualificationId || '',
  modules: overrides.modules !== undefined ? overrides.modules : (attachment.modules || []),
  units: overrides.units !== undefined ? overrides.units : (attachment.units || []),
  includeInCV:
    overrides.includeInCV !== undefined ? overrides.includeInCV : !!attachment.includeInCV,
  isQualification:
    overrides.isQualification !== undefined
      ? overrides.isQualification
      : attachment.isQualification !== undefined
        ? attachment.isQualification
        : true,
});

// Function to add media content to any section (qualification, top level videos)
export const addMediaContent = async (mediaData, contentPurpose = 'qualification') => {
  const token = localStorage.getItem('token');
  
  try {
    
    // Handle Bio Video separately
    if (contentPurpose === 'bioVideo' && mediaData.type === 'video') {
      return await addBioVideo(mediaData);
    }
    
    // Handle top level videos
    if (contentPurpose === 'topLevelVideos' && mediaData.type === 'video') {
      const requestData = {
        title: mediaData.title,
        description: mediaData.description,
        url: mediaData.media_path, // Convert media_path to url for top level videos
        type: 'video',
        thumbnail: mediaData.thumbnail || null // poster image for fast first paint
      };
      
      const response = await axios.post(`${API_BASE_URL}/topLevelVideo`, requestData, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Invalidate caches after successful add
      apiCache.invalidate('top-level-videos');
      apiCache.invalidate('user-data');

      return response.data;
    }

    // Handle qualification content (modules and units) - use OLD working approach
    if (contentPurpose === 'qualification') {
      
      // Prepare the request payload exactly like your working Postman example
      const requestData = {
        title: mediaData.title,
        description: mediaData.description,
        type: mediaData.type,
        media_path: mediaData.media_path,
        comments: mediaData.comments || '',
        post_to: mediaData.post_to, // qualification ID
        modules: mediaData.modules || [], // module IDs array
        units: mediaData.units || [], // unit IDs array
        includeInCV: mediaData.includeInCV || false,
        isQualification: true
      };
      
      const response = await axios.post(`${API_BASE_URL}/content`, requestData, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Invalidate user data cache so refreshes pick up the new content
      apiCache.invalidate('user-data');
      return response.data;
    }

    // Handle video gallery
    if (contentPurpose === 'videoGallery' && mediaData.type === 'video') {
      const requestData = {
        title: mediaData.title,
        description: mediaData.description,
        url: mediaData.media_path,
        type: 'video',
        isVideoGallery: true
      };
      
      const response = await axios.post(CONTENT_API_URL, requestData, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      });

      apiCache.invalidate('user-data');
      return response.data;
    }

    // Fallback - shouldn't reach here
    throw new Error(`Unsupported content purpose: ${contentPurpose}`);
    
  } catch (error) {
    console.error(`Error adding ${contentPurpose} content:`, error.response?.data || error.message);
    console.error('Full error object:', error);
    throw new Error(error.response?.data?.message || `Error adding ${contentPurpose} content`);
  }
};

// Function to update media content
export const updateMediaContent = async (mediaId, updateData, contentPurpose = 'qualification') => {
  const token = localStorage.getItem('token');
  
  try {
    
    // Handle top level videos using the correct update endpoint
    if (contentPurpose === 'topLevelVideos') {
      
      // Use the video-gallery update endpoint with the correct payload format
      const requestData = {
        id: mediaId,
        title: updateData.title,
        description: updateData.description,
        type: 'video',
        url: updateData.url || updateData.media_path, // Handle both field names
        ...(updateData.thumbnail !== undefined ? { thumbnail: updateData.thumbnail } : {}) // poster backfill (omit when not provided so edits don't clobber)
      };
      
      
      const response = await axios.put(`${API_BASE_URL}/update/video-gallery`, requestData, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    }
    
    // Handle qualification content updates
    if (contentPurpose === 'qualification') {

      const baseAttachment = updateData._attachment || {
        id: mediaId,
        post_to: updateData.post_to || updateData.qualification_id || updateData.qualificationId,
        modules: updateData.modules,
        units: updateData.units,
        type: updateData.type,
        media_path: updateData.media_path || updateData.url,
        title: updateData.title,
        description: updateData.description,
        comments: updateData.comments,
        includeInCV: updateData.includeInCV,
        isQualification: updateData.isQualification,
      };

      const { _attachment, ...overrides } = updateData;
      const requestData = buildQualificationUpdatePayload(
        { ...baseAttachment, id: mediaId },
        overrides
      );

      const response = await axios.put(`${API_BASE_URL}/content`, requestData, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        }
      });

      apiCache.invalidate('user-data');
      return response.data;
    }
    
    // Fallback for other content types
    throw new Error(`Update not supported for content purpose: ${contentPurpose}`);
    
  } catch (error) {
    console.error(`Error updating ${contentPurpose} content:`, error.response?.data || error.message);
    console.error('Full error object:', error);
    throw new Error(error.response?.data?.message || `Error updating ${contentPurpose} content`);
  }
};
// Function to delete video using the deleteVideoList API
export const deleteVideo = async (videoId, videoType) => {
  const token = localStorage.getItem('token');
  
  try {
    const response = await axios.delete(`${API_BASE_URL}/deleteVideoList/delete`, {
      params: {
        videoId,
        videoType
      },
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error(`Error deleting ${videoType} video:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.message || `Error deleting ${videoType} video`);
  }
};

export const updateQualificationAttachment = async (attachment, overrides = {}) => {
  return updateMediaContent(attachment.id, { _attachment: attachment, ...overrides }, 'qualification');
};

export const toggleIncludeInCV = async (attachment, includeInCV) => {
  const token = localStorage.getItem('token');
  const payload = buildQualificationUpdatePayload(attachment, { includeInCV });
  
  try {
    const response = await axios.put(`${API_BASE_URL}/content`, payload, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json'
      },
    });
    
    apiCache.invalidate('user-data');

    // Check if backend returned an attachment with an ID
    if (response.data?.result?.id) {
    } else if (response.data?.id) {
    } else {
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ Error updating includeInCV:', error.response?.data || error.message);
    console.error('Full error object:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to update CV setting');
  }
};

export const deleteAttachment = async (attachmentId) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  
  try {
    
    const response = await axios.delete(`${API_BASE_URL}/content?id=${attachmentId}`, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error deleting attachment:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message);
  }
};

// Then use it for removeAttachment
export const removeAttachment = async (deleteData) => {
  const token = localStorage.getItem('token');
  
  try {
    
    const requestPayload = {
      attachmentId: deleteData.attachmentId,
      qualificationId: deleteData.qualificationId,
      isQualification: deleteData.isQualification || false
    };

    if (deleteData.unitId) {
      requestPayload.unitId = deleteData.unitId;
    } else if (deleteData.moduleId) {
      requestPayload.moduleId = deleteData.moduleId;
    }

    
    const response = await axios.delete(`${API_BASE_URL}/content?id=${requestPayload.attachmentId}`, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error removing attachment:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message);
  }
};

// Legacy function for deleting media content (keep for backward compatibility)
export const deleteMediaContent = async (mediaId, contentPurpose = 'qualification') => {
  const token = localStorage.getItem('token');
  
  try {
    let endpoint = 'removeAttachment';
    
    // Determine the appropriate endpoint based on content purpose
    if (contentPurpose === 'topLevelVideos') {
      endpoint = 'topLevelVideo';
    }
    
    const response = await axios.delete(`${API_BASE_URL}/${endpoint}/${mediaId}`, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error(`Error deleting ${contentPurpose} content:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.message || `Error deleting ${contentPurpose} content`);
  }
};

// Replace your deleteBioVideo function with this:

export const deleteBioVideo = async () => {
  const token = localStorage.getItem('token');
  
  try {
    // First, get the bio video to extract its ID
    const bioVideo = await getBioVideo();
    
    if (!bioVideo || !bioVideo.id) {
      throw new Error('No bio video found to delete');
    }
    
    
    // Use the exact same pattern as your Postman request
    const response = await axios.delete(`${API_BASE_URL}/deleteVideoList/delete`, {
      params: {
        videoId: bioVideo.id,
        videoType: 'bioVideo'  // Changed from 'topLevelVideos' to 'bioVideo'
      },
      headers: {
        'Authorization': `${token}`,
      },
    });
    

    // Invalidate bio video cache after successful delete
    apiCache.invalidate('bio-video');

    return response.data;
  } catch (error) {
    console.error('Error deleting bio video:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Error deleting bio video');
  }
};